import { Vector2 } from "../../common/vector2.js";
import { randomRange, randomSign } from "../../common/common_funcs.js";
import { randomPointInCircle } from "../../common/visual_fx_common_funcs.js";
import { Game } from "../../game.js";
import { ImgID } from "../../image_loader/img_id.js";
import { Easing } from "../../transforms/transform_base.js";
import { TransformOpacity } from "../../transforms/transform_opacity.js";
import { TransformRotate } from "../../transforms/transform_rotate.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { TransformValue } from "../../transforms/transform_value.js";
import { PayloadEmitter } from "../../webgl_2d/core/emitters/payload_emitter.js";
import { CatmulromPrecomputed } from "../../webgl_2d/utils/catmulrom_precomputed.js";
import { VfxParticleAdditiveBatch, VfxParticleNormalBatch } from "../../webgl_2d/vfx/particle/vfx_particle_batch.js";
import { Vfx2DManager } from "../../webgl_2d/vfx_2d_manager.js";
import { Vfx2DRuntime } from "../../webgl_2d/vfx_2d_runtime.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { RewardFlyToHud } from "./reward_fly_to_hud.js";

export class StarFly
{
  private static readonly STAR_SIZE_DESIGN = 72;
  private static readonly MIN_DURATION_MS = 650;
  private static readonly MAX_DURATION_MS = 900;
  private static readonly DELAY_MS = 70;
  private static readonly SPAWN_RADIUS = 20;
  private static readonly MIN_AMPLITUDE = 40;
  private static readonly MAX_AMPLITUDE = 220;
  private static readonly ACCELERATION_POWER = 2;
  private static readonly START_SCALE = 1.2;
  private static readonly END_SCALE = 0.5;
  private static readonly SPIN_TURNS = 0.75;
  private static readonly COLOR = { x: 1.0, y: 0.85, z: 0.3 };

  private static readonly TRAIL_IMG_ID = ImgID.CircleGlow3;
  private static readonly TRAIL_LIFESPAN_MS = 300;
  private static readonly TRAIL_START_SIZE = 70;
  private static readonly TRAIL_END_SIZE = 30;
  private static readonly TRAIL_START_ALPHA = 0.35;
  private static readonly TRAIL_RATE_OVER_DIST = 0.05;

  private static readonly SPARK_IMG_ID = ImgID.SparkGlow2;
  private static readonly SPARK_LIFESPAN_MS = 380;
  private static readonly SPARK_SIZE = 34;
  private static readonly SPARK_BREAKPOINT = 0.2;
  private static readonly SPARK_RADIUS = 45;
  private static readonly SPARK_COUNT = 3;
  private static readonly SPARK_RATE_OVER_DIST = 0.012;

  private static readonly IMPACT_MIN_MS = 300;
  private static readonly IMPACT_EXTRA_MS = 250;

  static play( ctx: CanvasRenderingContext2D, root: UIElement, from_abs: Vector2, to_abs: Vector2, count: number, start_time: number, on_arrived?: () => void ): number
  {
    Game.instance.setVfxOverlayCanvasesVisible( true );

    const scale = LayoutScaler.getScale();
    const origin = root.getAbsolutePosition();
    const size = LayoutScaler.widthFromDesign( StarFly.STAR_SIZE_DESIGN );
    const normal_batch = Vfx2DRuntime.onOverlayFront( VfxParticleNormalBatch );
    const additive_batch = Vfx2DRuntime.onOverlayFront( VfxParticleAdditiveBatch );
    const ease = ( t: number ): number => Math.pow( t, StarFly.ACCELERATION_POWER );

    let first_arrival = Infinity;
    let last_arrival = start_time;

    for ( let i = 0; i < count; i++ )
    {
      const spawn = randomPointInCircle( from_abs, StarFly.SPAWN_RADIUS * scale );
      const amplitude = randomRange( StarFly.MIN_AMPLITUDE, StarFly.MAX_AMPLITUDE ) * randomSign() * scale;
      const precomputed = CatmulromPrecomputed.calcPrecomputed( spawn, to_abs, amplitude, { mode: "perpendicular_normal" } );
      const duration = randomRange( StarFly.MIN_DURATION_MS, StarFly.MAX_DURATION_MS );
      const delay = start_time + i * StarFly.DELAY_MS;
      const arrival = delay + duration;
      first_arrival = Math.min( first_arrival, arrival );
      last_arrival = Math.max( last_arrival, arrival );

      const star = new UIImage( ctx, spawn.x - origin.x - size / 2, spawn.y - origin.y - size / 2, size, size, ImgID.UI_DailyTasksProgressStar );
      star.setClickable( false );
      star.enableRotation();
      root.addChild( star );

      const star_path = ( t: number ): Vector2 =>
      {
        const p = precomputed.catmullRomAtT( ease( t ) );
        return new Vector2( p.x - origin.x - size / 2, p.y - origin.y - size / 2 );
      };
      const fly = new TransformValue( 0, 1, delay, duration, t => star.setPixelPos( star_path( t ), true ), { easing: Easing.Linear } );
      fly.setDomSample( t => ( { pos: star_path( t ) } ) );
      star.addTransform( fly );
      star.addTransform( new TransformRotate( star, true, 0, randomSign() * Math.PI * 2 * StarFly.SPIN_TURNS, delay, duration, { easing: Easing.Linear } ) );
      star.addTransform( new TransformScale(
        star,
        true,
        new Vector2( StarFly.START_SCALE, StarFly.START_SCALE ),
        new Vector2( StarFly.END_SCALE, StarFly.END_SCALE ),
        delay,
        duration,
        { easing: Easing.EaseInQuad }
      ) );
      const fade = new TransformOpacity( star, true, 1, 0, delay + duration * 0.85, duration * 0.15, { easing: Easing.EaseInQuad } );
      fade.setOnFinished( () =>
      {
        root.removeChild( star );
        root.forceMakeShouldBeRendered();
      } );
      star.addTransform( fade );

      normal_batch.addEmitter( new PayloadEmitter(
        spawn,
        delay,
        {
          color: StarFly.COLOR,
          precomputed,
          ease,
          config: {
            img_id: StarFly.TRAIL_IMG_ID,
            particle_life_span: StarFly.TRAIL_LIFESPAN_MS,
            start_size: StarFly.TRAIL_START_SIZE * scale,
            end_size: StarFly.TRAIL_END_SIZE * scale,
            start_alpha: StarFly.TRAIL_START_ALPHA,
            end_alpha: 0,
            breakpoint: 0,
          },
        },
        duration,
        { rate_over_distance: StarFly.TRAIL_RATE_OVER_DIST / scale }
      ) );

      additive_batch.addEmitter( new PayloadEmitter(
        spawn,
        delay,
        {
          color: StarFly.COLOR,
          precomputed,
          ease,
          config: {
            img_id: StarFly.SPARK_IMG_ID,
            particle_life_span: StarFly.SPARK_LIFESPAN_MS,
            start_size: StarFly.SPARK_SIZE * scale,
            end_size: StarFly.SPARK_SIZE * scale,
            start_alpha: 1,
            end_alpha: 0,
            breakpoint: StarFly.SPARK_BREAKPOINT,
            circle_radius: StarFly.SPARK_RADIUS * scale,
          },
        },
        duration,
        { rate_over_distance: StarFly.SPARK_RATE_OVER_DIST / scale, count: StarFly.SPARK_COUNT }
      ) );
    }

    if ( count > 0 )
    {
      const impact_duration = Math.max( StarFly.IMPACT_MIN_MS, last_arrival - first_arrival + StarFly.IMPACT_EXTRA_MS );
      Vfx2DManager.addCoinCounterImpact( to_abs, size * 1.6, first_arrival, impact_duration );
    }

    RewardFlyToHud.schedule( ctx, root, start_time, last_arrival, () =>
    {
      on_arrived?.();
      RewardFlyToHud.releaseVfxOverlay();
    } );

    return last_arrival;
  }
}
